import { useState, useEffect, useRef } from "react";
import "./Layout.css";
import ArrowDown from "../../assets/icons/Group 33921.svg";
import { ChevronDown } from "lucide-react";
import Breadcrumsv2 from "../../components/breadcrums_v2/Breadcrums";
import CategoryNav from "../../components/categorynav_v2/CategoryNav";
import BrandNav from "../../components/brandnav_v2/BrandNav";
import AttributeNav from "../../components/attributenav_v2/AttributeNav";
import { useLocation, useSearchParams } from "react-router-dom";
import FallbackImg from "../FallbackImg";

function Layout({
  children,
  brandList = [],
  productGroupList = [],
  attributeList = [],
}) {
  const location = useLocation();
  const [clearInput, setClearInput] = useState(false);
  const dropdownOptions = [
    { label: "All", value: "all" },
    { label: "Available Stock", value: "instock" },
    { label: "Out of Stock", value: "outofstock" },
    { label: "Discontinued", value: "discontinued" },
    { label: "Price - Low to High", value: "price_asc" },
    { label: "Price - High to Low", value: "price_desc" },
  ];
  const [dropdownValue, setDropdownValue] = useState(dropdownOptions[0].value);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const stockFilter = searchParams.get("stock_filter");
    const discontinuedOnly = searchParams.get("discontinued_only");
    const sort = searchParams.get("sort");
    if (discontinuedOnly === "1") {
      setDropdownValue("discontinued");
    } else if (stockFilter === "instock") {
      setDropdownValue("instock");
    } else if (stockFilter === "outofstock") {
      setDropdownValue("outofstock");
    } else if (sort === "price_asc") {
      setDropdownValue("price_asc");
    } else if (sort === "price_desc") {
      setDropdownValue("price_desc");
    } else {
      setDropdownValue("all");
    }
  }, [searchParams]);

  // Auto-open brand and product dropdowns when both params are empty
  useEffect(() => {
    const brandsParam = searchParams.get("brands");
    const productParam = searchParams.get("product");

    // If both brands and product parameters are empty/null, trigger auto-open
    if (!brandsParam && !productParam) {
      setClearInput("openDropdowns");
    }
  }, [searchParams]);

  // Get filter_type from URL
  const params = new URLSearchParams(location.search);
  const filterType = params.get("filter_type");
  const isPackageFilter = filterType === "package";

  const handleDropdownSelect = (val) => {
    setDropdownValue(val);
    setDropdownOpen(false);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", 1);
    newParams.delete("stock_filter");
    newParams.delete("discontinued_only");
    newParams.delete("sort");
    if (val === "instock" || val === "outofstock") {
      newParams.set("stock_filter", val);
    } else if (val === "discontinued") {
      newParams.set("discontinued_only", "1");
    } else if (val === "price_asc" || val === "price_desc") {
      newParams.set("sort", val);
    }
    // "all" 不加任何参数
    setSearchParams(newParams);
  };

  const selectedLabel =
    dropdownOptions.find((opt) => opt.value === dropdownValue)?.label ||
    dropdownOptions[0].label;

  // Disable browser scrolling when the dropdown is open
  useEffect(() => {
    if (dropdownOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [dropdownOpen]);

  return (
    <div className="layout_v2">
      <Breadcrumsv2 />

      <div className="layout-content_v2">
        <div className="layout-dropdown-container_v2">
          <div className="layout-dropdown_v2">
            <span>SORT BY :</span>
            <div
              className="layout-dropdown-selected_v2"
              ref={dropdownRef}
              tabIndex={0}
              onClick={() => setDropdownOpen((open) => !open)}
            >
              <span className="layout-dropdown-label_v2">{selectedLabel}</span>
              <ChevronDown size={16} className={dropdownOpen ? "rotate" : ""} />
              {dropdownOpen && (
                <>
                  <div className="custom-dropdown-overlay_v2" />
                  <div className="layout-dropdown-list_v2">
                    {dropdownOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`layout-dropdown-option_v2${opt.value === "price_asc" ? " separator" : ""}${
                          dropdownValue === opt.value ? " selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDropdownSelect(opt.value);
                        }}
                      >
                        <span>{opt.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="nav-container_v2">
          {isPackageFilter ? (
            <div className="package-main_v2">
              {typeof children === "function"
                ? children({
                    brandList,
                    productGroupList,
                    attributeList,
                    clearInput,
                    setClearInput,
                    dropdownValue,
                  })
                : children}
            </div>
          ) : (
            <>
              <div className="nav_v2">
                <BrandNav
                  brandList={brandList}
                  clearInput={clearInput}
                  setClearInput={setClearInput}
                  disabled={isPackageFilter}
                />
                <CategoryNav
                  productGroupList={productGroupList}
                  clearInput={clearInput}
                  setClearInput={setClearInput}
                  disabled={isPackageFilter}
                />
                <AttributeNav
                  attributeList={attributeList}
                  disabled={isPackageFilter}
                />
              </div>

              <div className="main_v2">
                {typeof children === "function"
                  ? children({
                      brandList,
                      productGroupList,
                      attributeList,
                      clearInput,
                      setClearInput,
                      dropdownValue,
                    })
                  : children}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Layout;
